// previewSession.js
//
// Tab-scoped "preview as manager" support for the internal maintenance
// hub. Lets a maintenance user click "Manager View" on a building and
// open the full manager dashboard for that building in a new tab — and
// stay there across every internal navigation, without polluting their
// own (maintenance-role) session in localStorage.
//
// How it works
// ------------
//   1. The new tab opens at `/?buildingId=<id>&preview=1` (see
//      MaintenanceHome.openBuildingPreview).
//   2. This module runs BEFORE React mounts (imported from index.js
//      ahead of <App />). On first load it sees the preview query
//      params, snapshots the real maintenance user from localStorage,
//      and writes a synthetic "preview manager user" into
//      sessionStorage with:
//          • buildingId  → the previewed building
//          • buildings   → [previewed building]  (some components read
//                          the plural form)
//          • role        → "manager"             (so role gates fire as
//                          if this were a real manager session)
//          • _previewMode: true                  (debug flag)
//   3. On EVERY subsequent load of this module in the same tab (e.g.
//      React Router re-mounts, hard refresh, etc.) it sees the
//      sessionStorage entry and installs a one-time monkey-patch on
//      Storage.prototype:
//          • getItem('managerUser') on localStorage → returns the
//            synthetic preview user.
//          • setItem('managerUser') on localStorage → no-op + warn.
//            Prevents a stray code path from overwriting the real
//            maintenance profile while in preview mode.
//      Every component that already reads `localStorage.getItem('managerUser')`
//      automatically sees the previewed building. No refactor needed.
//   4. sessionStorage is per-tab. Closing the preview tab clears
//      everything; the original /maintenance tab is untouched.
//
// Caveats
// -------
//   • The bearer token in `managerToken` is still the maintenance
//     user's token. Backend permission checks must allow the
//     maintenance role to read building data (they do today — the
//     hub already calls /api/buildings, /api/getTechStartTimes, etc.).
//   • Components that read managerUser via property access
//     (`localStorage.managerUser`) bypass getItem and won't see the
//     override. As of this writing nothing in src/ does that — every
//     read goes through `localStorage.getItem('managerUser')`. If
//     someone adds a property-style read later, this comment will
//     hopefully save them an hour of debugging.

const PREVIEW_USER_KEY = 'previewManagerUser';
const PREVIEW_BUILDING_KEY = 'previewBuildingId';
const PREVIEW_FLAG_KEY = 'previewMode';

let patchInstalled = false;

function snapshotPreviewUserFromUrl() {
  if (typeof window === 'undefined') return;
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (_) {
    return;
  }
  const previewBuildingId = (params.get('buildingId') || '').trim();
  const isPreview = params.get('preview') === '1';
  if (!previewBuildingId || !isPreview) return;

  // Don't clobber an existing preview snapshot — if the user navigated
  // back to "/?buildingId=X&preview=1" intentionally we still want the
  // first snapshot to win so logs/IDs stay stable across the session.
  if (window.sessionStorage.getItem(PREVIEW_USER_KEY)) return;

  let realUser = null;
  try {
    const raw = window.localStorage.getItem('managerUser');
    if (raw) realUser = JSON.parse(raw);
  } catch (_) {
    realUser = null;
  }
  if (!realUser) return;

  const previewUser = {
    ...realUser,
    buildingId: previewBuildingId,
    buildings: [previewBuildingId],
    role: 'manager',
    _previewMode: true,
    _previewOriginalRole: realUser.role || '',
    _previewOriginalUsername: realUser.username || '',
  };
  window.sessionStorage.setItem(PREVIEW_USER_KEY, JSON.stringify(previewUser));
  window.sessionStorage.setItem(PREVIEW_BUILDING_KEY, previewBuildingId);
  window.sessionStorage.setItem(PREVIEW_FLAG_KEY, 'true');
}

function installPatch() {
  if (patchInstalled) return;
  if (typeof window === 'undefined') return;
  if (!window.sessionStorage.getItem(PREVIEW_USER_KEY)) return;

  const origGetItem = Storage.prototype.getItem;
  const origSetItem = Storage.prototype.setItem;

  Storage.prototype.getItem = function patchedGetItem(key) {
    if (this === window.localStorage && key === 'managerUser') {
      const override = window.sessionStorage.getItem(PREVIEW_USER_KEY);
      if (override) return override;
    }
    return origGetItem.call(this, key);
  };

  Storage.prototype.setItem = function patchedSetItem(key, value) {
    if (this === window.localStorage && key === 'managerUser') {
      // Silently swallow writes while previewing. The real
      // maintenance-user profile in localStorage stays intact.
      // We DO still mirror the write into sessionStorage so any
      // component that round-trips set → get sees its own update.
      try {
        window.sessionStorage.setItem(PREVIEW_USER_KEY, value);
      } catch (_) { /* quota — fall through */ }
      // eslint-disable-next-line no-console
      console.warn(
        '[previewSession] Intercepted localStorage.setItem("managerUser") in preview mode; mirrored to sessionStorage only.',
      );
      return;
    }
    return origSetItem.call(this, key, value);
  };

  patchInstalled = true;
  // eslint-disable-next-line no-console
  console.log(
    '[previewSession] Active — previewing building',
    window.sessionStorage.getItem(PREVIEW_BUILDING_KEY),
  );
}

export function isPreviewMode() {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(PREVIEW_FLAG_KEY) === 'true';
}

export function getPreviewBuildingId() {
  if (typeof window === 'undefined') return '';
  return window.sessionStorage.getItem(PREVIEW_BUILDING_KEY) || '';
}

export function exitPreviewMode() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(PREVIEW_USER_KEY);
    window.sessionStorage.removeItem(PREVIEW_BUILDING_KEY);
    window.sessionStorage.removeItem(PREVIEW_FLAG_KEY);
  } catch (_) { /* ignore */ }
  // Easiest, safest way to drop the monkey-patch + reset every
  // component's in-memory copy of managerUser: close the tab if we
  // can (it was opened via window.open from /maintenance), otherwise
  // hard-reload to a clean state.
  try {
    window.close();
  } catch (_) { /* not openable */ }
  // If close() didn't actually close the tab (some browsers block it
  // for tabs not opened by script), bounce back to /maintenance.
  setTimeout(() => {
    try {
      window.location.replace('/maintenance');
    } catch (_) { /* ignore */ }
  }, 50);
}

// Run snapshot + patch at module-load time. Order matters: snapshot
// first so installPatch sees the sessionStorage entry on the very
// first load (the one with ?preview=1 in the URL).
snapshotPreviewUserFromUrl();
installPatch();
